import { Component, Input, OnDestroy, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { distinctSubject } from '../../utils/rx.utils';
import { RenderingContext, RenderingContextProvider } from './../../../api';
import {
  createSingleSubject,
  DEFAULT_FETCH_LEVELS,
  DEFAULT_LAYOUT_MODE,
  KEY_LAYOUT_MODE,
  KEY_LEVELS,
  opBoxLayoutMode,
  opDistinctUntilChanged,
  opLevels,
  opShareLast,
  perfMeasure,
  rxPipe,
} from './../../../utils';
import { WchService } from './../../services/wch.service';
import { defineProperties } from './../../utils/js.utils';
import { AbstractLifeCycleComponent, createSetter } from './../rendering/abstract.lifecycle.component';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'ContentComponent';

/**
 * Combines the active pieces of the state into one single object
 */
export declare type ComponentState = [RenderingContext, string];

@Component({
  selector: 'wch-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.css'],
  preserveWhitespaces: false
})
export class ContentComponent extends AbstractLifeCycleComponent
  implements OnDestroy, RenderingContextProvider {
  /*
   * Accepts the ID of the addressed component as an input
   */
  @Input()
  id: string;

  /*
   * Set the layout mode to render this page
   *
   * @param aValue the new layout mode, if nothing is given this defaults to 'default'
   */
  @Input()
  layoutMode: string;

  /*
   * Accepts the number of levels to be queried
   */
  @Input()
  levels: string | number | null | undefined;

  /*
   * This monitors the current rendering context
   */
  @Output()
  readonly onRenderingContext: Observable<RenderingContext>;

  /*
   * Event that propagates the newly created component
   */
  @Output()
  readonly onComponent: ReplaySubject<any> = createSingleSubject();

  /*
   * Fires whenever the layout mode changed, includes the initial default mode.
   */
  @Output()
  readonly onLayoutMode: Observable<string>;

  /**
   * The component state
   */
  readonly onState: Observable<ComponentState>;

  /*
   * Initialize the component and build the streams
   *
   * @param aWchService the WCH service to get the context from
   */
  constructor(aWchService: WchService) {
    super();
    // back reference
    const that = this;

    // measure startup
    const handle = perfMeasure(LOGGER);

    // the destroy handler
    const opUntilDestroyed: {
      <T>(src: Observable<T>): Observable<T>;
    } = takeUntil(that.onOnDestroy);

    // handle the query levels
    const levelsSubject = new BehaviorSubject<
      string | number | null | undefined
    >(DEFAULT_FETCH_LEVELS);

    const onLevels = rxPipe(levelsSubject, opLevels, opDistinctUntilChanged);

    // the subjects
    const modeSubject = new BehaviorSubject<string>(DEFAULT_LAYOUT_MODE);

    // define the setters
    const onLayoutMode = rxPipe(
      modeSubject,
      opBoxLayoutMode,
      opDistinctUntilChanged
    );

    const idSubject = createSingleSubject<string>();

    const onId = distinctSubject(idSubject);

    // build the chain
    const onRenderingContext: Observable<RenderingContext> = rxPipe(
      combineLatest(onId, onLevels),
      // fetch the context from our service
      switchMap(([id, levels]) =>
        aWchService.getRenderingContextById(id, levels)
      ),
      // cache the last value
      opShareLast
    );

    /**
     * Combines the latest changes
     */
    const onComponentState = (this.onState = combineLatest(
      onRenderingContext,
      onLayoutMode
    ));

    // define the setters
    defineProperties(that, {
      id: createSetter(idSubject, that),
      [KEY_LEVELS]: createSetter(levelsSubject, that),
      [KEY_LAYOUT_MODE]: createSetter(modeSubject, that)
    });

    // define the events
    this.onRenderingContext = rxPipe(onRenderingContext, opUntilDestroyed);
    this.onLayoutMode = rxPipe(onLayoutMode, opUntilDestroyed);
  }

  // just dispatch, otherwise the method will not be recognized
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
